<template>
  <div
    v-if="visible"
    class="friendly-prompt-popup__overlay"
    :style="{ zIndex }"
    @click.self="$emit('close')"
  >
    <section
      :class="['friendly-prompt-popup', isPC ? 'is-pc' : 'is-h5']"
      role="dialog"
      aria-modal="true"
      :aria-label="content.title"
    >
      <img
        class="friendly-prompt-popup__header"
        :src="content.headerImageSrc"
        alt=""
      />
      <div class="friendly-prompt-popup__body">
        <div class="friendly-prompt-popup__copy">
          <img
            class="friendly-prompt-popup__title-image"
            :src="content.titleImageSrc"
            :alt="content.title"
          />
          <p class="friendly-prompt-popup__text">{{ content.body }}</p>
        </div>
        <button
          type="button"
          class="friendly-prompt-popup__button"
          @click="$emit('close')"
        >
          Got it
        </button>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { computed } from 'vue-demi';
import type { FlashSalePromptType } from '../types';
import baseballSuccessHeaderSrc from '../assets/baseball-success-header.png';
import titleEntryAlreadyReceivedSrc from '../assets/title-entry-already-received.png';
import titleNoLotteryEntriesSrc from '../assets/title-no-lottery-entries.png';
import titleSomethingWentWrongSrc from '../assets/title-something-went-wrong.png';
import titleWaitingForWinSrc from '../assets/title-waiting-for-win.png';
type FriendlyPromptContent = {
  title: string;
  titleImageSrc: string;
  body: string;
  headerImageSrc: string;
};
const __sfc_main = {};
__sfc_main.props = {
  visible: {
    key: "visible",
    required: true,
    type: Boolean
  },
  type: {
    key: "type",
    required: true,
    type: null
  },
  isPC: {
    key: "isPC",
    required: true,
    type: Boolean
  }
};
__sfc_main.setup = (__props, __ctx) => {
  const {
    useZIndex,
    useTeleport
  } = (require('@fegobull/webapi') as any).use;
  useTeleport(document.body);
  const zIndex = useZIndex();
  const props = __props;
  const PROMPT_CONTENT_MAP: Record<FlashSalePromptType, FriendlyPromptContent> = {
    waiting_for_win: {
      title: 'Waiting for a Win',
      titleImageSrc: titleWaitingForWinSrc,
      body: 'Every home victory unlocks a free chance. Come back after the next victory to take your shot at the $2M.',
      headerImageSrc: baseballSuccessHeaderSrc
    },
    no_entries: {
      title: 'No lottery entries available',
      titleImageSrc: titleNoLotteryEntriesSrc,
      body: 'Every home victory unlocks a free shot at the $2 Million Prize Pool.',
      headerImageSrc: baseballSuccessHeaderSrc
    },
    already_received: {
      title: 'Entry Already Received',
      titleImageSrc: titleEntryAlreadyReceivedSrc,
      body: 'You have already submitted your entry for this round. One chance is allowed for each Mets home victory. We look forward to seeing you again after the next home win!',
      headerImageSrc: baseballSuccessHeaderSrc
    },
    something_wrong: {
      title: 'Something Went Wrong',
      titleImageSrc: titleSomethingWentWrongSrc,
      body: "Our system hit a little snag. Don't worry, your chance to draw is still valid. Please try again!",
      headerImageSrc: baseballSuccessHeaderSrc
    }
  };
  const content = computed(() => PROMPT_CONTENT_MAP[props.type ?? 'something_wrong']);
  return {
    zIndex,
    content
  };
};
export default __sfc_main;
</script>

<style scoped>
.friendly-prompt-popup__overlay {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  font-family: BR Hendrix, Arial, sans-serif;
}

.friendly-prompt-popup {
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  overflow: hidden;
  background: #ffffff;
}

.friendly-prompt-popup__header {
  display: block;
  width: 100%;
  height: auto;
  object-fit: cover;
}

.friendly-prompt-popup__body,
.friendly-prompt-popup__copy {
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
}

.friendly-prompt-popup__title-image,
.friendly-prompt-popup__text {
  margin: 0;
  text-align: center;
}

.friendly-prompt-popup__title-image {
  display: block;
  width: auto;
  max-width: 100%;
  max-height: 150px;
  margin-top: 40px;
}

.friendly-prompt-popup__text {
  color: #525252;
  font-weight: 400;
  line-height: 1.4;
}

.friendly-prompt-popup__button {
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  padding: 0;
  border: none;
  background: #ff6900;
  color: #ffffff;
  font-family: BR Hendrix, Arial, sans-serif;
  font-weight: 600;
  cursor: pointer;
}

/* H5:设计稿 100px = 1rem */
.friendly-prompt-popup.is-h5 {
  width: 6.3rem;
  border-radius: 0.4rem;
}

.friendly-prompt-popup.is-h5 .friendly-prompt-popup__header {
  height: 3.66rem;
}

.friendly-prompt-popup.is-h5 .friendly-prompt-popup__body {
  width: 100%;
  padding: 0 0.4rem 0.4rem;
  gap: 0.4rem;
}

.friendly-prompt-popup.is-h5 .friendly-prompt-popup__copy {
  width: 5.6rem;
  gap: 0.2rem;
}

.friendly-prompt-popup.is-h5 .friendly-prompt-popup__text {
  font-size: 0.3rem;
}

.friendly-prompt-popup.is-h5 .friendly-prompt-popup__button {
  width: 5rem;
  height: 0.9rem;
  border-radius: 0.45rem;
  font-size: 0.32rem;
}

.friendly-prompt-popup.is-pc {
  width: 420px;
  border-radius: 24px;
}

.friendly-prompt-popup.is-pc .friendly-prompt-popup__header {
  height: 244px;
}

.friendly-prompt-popup.is-pc .friendly-prompt-popup__body {
  width: 100%;
  padding: 0 28px 28px;
  gap: 24px;
}

.friendly-prompt-popup.is-pc .friendly-prompt-popup__copy {
  gap: 12px;
}

/* PC 标题图按视觉稿限制高度，避免复用 H5 最大高度导致标题过高。 */
.friendly-prompt-popup.is-pc .friendly-prompt-popup__title-image {
  max-height: 0.92rem;
  margin-top: 0.32rem;
}

.friendly-prompt-popup.is-pc .friendly-prompt-popup__text {
  font-size: 18px;
}

.friendly-prompt-popup.is-pc .friendly-prompt-popup__button {
  width: 320px;
  height: 56px;
  border-radius: 28px;
  font-size: 20px;
}
</style>
